import { NumberInput, Grid, Button } from "@mantine/core";
import { Modal } from "@mantine/core";
import { useContext, useEffect } from "react";
import { OrderContext } from "../../../context/OrderContext";
import { requiredFieldFormValidator } from "../../../utils/validators/formValidators";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import ProductType from "../../../enum/ProductType";
import { useFormContext } from "react-hook-form";
import {
    normalizeAttributesForLineType,
    normalizeProductIdentity,
    normalizeSelectedOptionIdentity,
    resolveLineId
} from "../utils/lineIdentity";
import { maybeRecomputePieceCount, parseOptionalPieceCount } from "../utils/pieceCount";
import { recalculateOrderProductsWithLineIds } from "../utils/recalculateOrderProducts";
import { normalizeOrderQuantity, validateOrderQuantity, type OrderQuantityType } from "../utils/orderQuantity";

const EditQuantityModal = ({ storeDiscounts }: { storeDiscounts: any }) => {
    const { t } = useTranslation(['NewOrder', 'FieldName', 'FormValidator']);
    const { setUpdatingQuantityProduct, updatingQuantityProduct, setUpdatingQuantityProductModal, updatingQuantityProductModal } = useContext(OrderContext);
    const { setValue, getValues } = useFormContext();
    const form = useForm({
        initialValues: {
            quantity: undefined,
            pieceCount: undefined
        },
        validate: {
            quantity: (value) => {
                const requiredValidatorRes = requiredFieldFormValidator(value);
                if (requiredValidatorRes) return requiredValidatorRes;
            }
        }
    });

    type FormValues = typeof form.values;
    const isLengthProduct = updatingQuantityProduct?.type === ProductType.LENGTH;
    const isMeasuredProduct = updatingQuantityProduct?.type === ProductType.WEIGHT || isLengthProduct;
    const orderQuantityType: OrderQuantityType = isLengthProduct ? 'LENGTH' : 'WEIGHT';

    const validateQuantityValue = (value: number | string | undefined | null): string | null => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
        if (isMeasuredProduct) {
            const result = validateOrderQuantity(value, orderQuantityType);
            return result.ok ? null : t('FormValidator:invalidQuantity');
        }
        if (value === undefined || value === null || value === '') return requiredFieldFormValidator(value);
        const num = typeof value === 'number' ? value : Number(value);
        return Number.isFinite(num) ? null : t('FormValidator:invalidQuantity');
    };

    const submit = async (values: FormValues) => {
        const products = getValues('products') || [];
        if (!updatingQuantityProduct || updatingQuantityProduct.returnSourceOrderLineId) return;
        const validationError = validateQuantityValue(values.quantity);
        if (validationError) {
            form.setFieldError('quantity', validationError);
            return;
        }
        let normalizedQuantity: number;
        if (isMeasuredProduct) {
            const normalized = normalizeOrderQuantity(values.quantity, orderQuantityType);
            if (normalized === null) {
                form.setFieldError('quantity', t('FormValidator:invalidQuantity'));
                return;
            }
            normalizedQuantity = normalized;
        } else {
            const raw = values.quantity;
            const num = typeof raw === 'number' ? raw : Number(raw);
            if (!Number.isFinite(num)) {
                form.setFieldError('quantity', t('FormValidator:invalidQuantity'));
                return;
            }
            normalizedQuantity = Math.floor(num);
        }
        const parsedPieceCount = isMeasuredProduct
            ? parseOptionalPieceCount(values.pieceCount)
            : { ok: true as const, value: null };
        if (!parsedPieceCount.ok) {
            form.setFieldError('pieceCount', t('FormValidator:invalidQuantity'));
            return;
        }

        const targetLineId = updatingQuantityProduct.lineId || updatingQuantityProduct.orderProductLineID;
        let prodIdx = -1;
        if (targetLineId) {
            prodIdx = products.findIndex((line: any) => resolveLineId(line) === targetLineId);
        } else {
            const targetId = normalizeProductIdentity(updatingQuantityProduct);
            const targetOptId = normalizeSelectedOptionIdentity(updatingQuantityProduct);
            prodIdx = products.findIndex((line: any) => (
                normalizeProductIdentity(line) === targetId &&
                normalizeSelectedOptionIdentity(line) === targetOptId
            ));
        }

        const productsCopy = [...products];
        if (prodIdx !== -1 && normalizedQuantity === 0) {
            productsCopy.splice(prodIdx, 1);
        } else if (prodIdx !== -1) {
            productsCopy[prodIdx] = maybeRecomputePieceCount({
                ...productsCopy[prodIdx],
                quantity: normalizedQuantity,
                pieceCount: parsedPieceCount.value,
                __pieceCountTouched: parsedPieceCount.value !== null,
                attributes: normalizeAttributesForLineType(
                    productsCopy[prodIdx],
                    normalizedQuantity
                )
            });
        }

        const { newOrderProds, buyAndGetProds } = recalculateOrderProductsWithLineIds({
            discountCodeObj: getValues('discountCodeObj'),
            products: productsCopy,
            express: getValues('express'),
            storeDiscounts,
            customerInfo: getValues('customerInfo'),
            previousBuyAndGetProducts: getValues('buyAndGetProducts'),
            orderType: getValues('orderType'),
            returnItemsChargeMode: getValues('returnItemsChargeMode')
        });
        setValue('products', newOrderProds);
        setValue('buyAndGetProducts', buyAndGetProds);
        setUpdatingQuantityProduct(undefined);
        setUpdatingQuantityProductModal(false);
    }

    useEffect(() => {
        form.setValues({
            quantity: (updatingQuantityProduct as any)?.quantity || undefined,
            pieceCount: isMeasuredProduct
                ? (updatingQuantityProduct as any)?.pieceCount ?? undefined
                : undefined
        });
        form.setErrors({});
    }, [isMeasuredProduct, updatingQuantityProduct]);

    return (
        <Modal
            title={`${updatingQuantityProduct?.name}`}
            opened={updatingQuantityProductModal}
            onClose={() => {
                setUpdatingQuantityProductModal(false);
                setUpdatingQuantityProduct(undefined);
            }}>
            <form onSubmit={form.onSubmit(submit)}>
                <Grid>
                    <Grid.Col span={12}>
                        <NumberInput
                            {...form.getInputProps('quantity')}
                            placeholder={t('FieldName:quantity')}
                            label={t('FieldName:quantity')}
                            allowLeadingZeros={false}
                            allowNegative
                            decimalScale={isLengthProduct ? 3 : 2}
                            allowedDecimalSeparators={['.']}
                            decimalSeparator="."
                            autoFocus
                        />
                    </Grid.Col>
                    {isMeasuredProduct && (
                        <Grid.Col span={12}>
                            <NumberInput
                                data-testid="edit-quantity-piece-count"
                                {...form.getInputProps('pieceCount')}
                                placeholder={t('FieldName:pieceCountOptional')}
                                label={t('FieldName:pieceCountOptional')}
                                allowDecimal={false}
                                allowNegative={false}
                                min={0}
                                decimalScale={0}
                            />
                        </Grid.Col>
                    )}
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                        <Button
                            fullWidth
                            color="gray"
                            onClick={() => {
                                setUpdatingQuantityProductModal(false);
                                setUpdatingQuantityProduct(undefined);
                            }}>
                            {t('CommonButtons:cancel')}
                        </Button>
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                        <Button fullWidth type="submit" >
                            {t('CommonButtons:submit')}
                        </Button>
                    </Grid.Col>
                </Grid>
            </form>

        </Modal>
    );
};

export default EditQuantityModal;
